/**
 * MLP (Major League Pickleball style) Scheduler
 *
 * Self-contained MLP schedule generator — everything MLP-specific (metrics,
 * game types, round/match building) lives in this one file rather than
 * reusing Custom Format's generic engine, because real MLP isn't a generic
 * "N games between two teams" format: every team-pairing plays a fixed
 * 5-game slate (Men's Doubles, Women's Doubles, Mixed Doubles 1, Mixed
 * Doubles 2, Dreambreaker), each game using a *specific* 2-player subset of
 * the team's 4-player roster, not an arbitrary/rotating pair.
 */

import { SWAP_OPTIONS } from '../../scoring/scoringConfig.js';

const generateMatchId = () => `mlp-match-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * The 5 fixed MLP game types, in play order.
 */
export const MLP_GAME_TYPES = ['mens_doubles', 'womens_doubles', 'mixed_1', 'mixed_2', 'dreambreaker'];
export const MLP_GAME_TYPE_LABELS = {
  mens_doubles: "Men's Doubles",
  womens_doubles: "Women's Doubles",
  mixed_1: 'Mixed Doubles 1',
  mixed_2: 'Mixed Doubles 2',
  dreambreaker: 'Dreambreaker',
};

/**
 * The 2 supported multi-team tournament structures — both require teams to
 * be split into groups, just with opposite matchup rules:
 * - 'group_playoffs' (Group Stage + Playoffs): round-robin *within* each
 *   group, then top finishers advance to a playoff round (teams TBD,
 *   resolved from standings once group play finishes).
 * - 'cross_group' (Cross-group / Dual Match): each group only faces the
 *   *other* group(s), never its own — standings-only, no playoff round.
 */
export const MLP_STRUCTURES = {
  GROUP_PLAYOFFS: 'group_playoffs',
  CROSS_GROUP: 'cross_group',
};

/**
 * Assigns each team to a group. Teams with an explicit assignment (via
 * `manualAssignments`, keyed by participant id) keep it; everyone else is
 * distributed to whichever group currently has the fewest teams. Returns an
 * array parallel to `teams`: groupIndex per team.
 */
export function assignGroups(teams, numberOfGroups, manualAssignments = null) {
  const assignment = new Array(teams.length).fill(null);
  const unassignedIndices = [];

  teams.forEach((team, idx) => {
    const manual = manualAssignments?.[team.id];
    if (manual != null && manual >= 0 && manual < numberOfGroups) {
      assignment[idx] = manual;
    } else {
      unassignedIndices.push(idx);
    }
  });

  const groupCounts = new Array(numberOfGroups).fill(0);
  assignment.forEach(g => { if (g != null) groupCounts[g]++; });

  unassignedIndices.forEach(idx => {
    let smallest = 0;
    for (let g = 1; g < numberOfGroups; g++) {
      if (groupCounts[g] < groupCounts[smallest]) smallest = g;
    }
    assignment[idx] = smallest;
    groupCounts[smallest]++;
  });

  return assignment;
}

/**
 * Splits a team's roster into 2 men + 2 women for the gender-specific game
 * types. Real gender comes from each player's profile (`player.gender` as
 * 'male'/'female') once that's wired through the roster fetch — until then
 * (or for any roster where it's missing/incomplete), falls back to an
 * arbitrary random 2/2 split so scheduling still works. Same {men, women}
 * shape either way, so plugging in real profile data later is a one-line
 * change at the call site, not a schema change here.
 */
export function splitRosterByGender(roster) {
  const allHaveGender = roster.length > 0 && roster.every(p => p.gender === 'male' || p.gender === 'female');
  if (allHaveGender) {
    return {
      men: roster.filter(p => p.gender === 'male'),
      women: roster.filter(p => p.gender === 'female'),
    };
  }
  const shuffled = shuffleArray(roster);
  return { men: shuffled.slice(0, 2), women: shuffled.slice(2, 4) };
}

/**
 * Builds the fixed 5-game slate for one team pairing, given each team's
 * (already gender-split) 2 men + 2 women. Mixed 1/2 and Dreambreaker pairings
 * are an arbitrary but consistent assignment (man[0]+woman[0], man[1]+woman[1],
 * man[0]+woman[1]) — real MLP lets team captains choose these live, which
 * doesn't fit a static pre-generated schedule, so this is a reasonable
 * placeholder pairing rather than an attempt to predict captains' choices.
 */
function buildMLPGamesForPairing(teamA, teamB, rosterA, rosterB) {
  const a = splitRosterByGender(rosterA);
  const b = splitRosterByGender(rosterB);
  return MLP_GAME_TYPES.map(gameType => {
    let participantAPlayers, participantBPlayers;
    switch (gameType) {
      case 'mens_doubles':
        participantAPlayers = a.men;
        participantBPlayers = b.men;
        break;
      case 'womens_doubles':
        participantAPlayers = a.women;
        participantBPlayers = b.women;
        break;
      case 'mixed_1':
        participantAPlayers = [a.men[0], a.women[0]].filter(Boolean);
        participantBPlayers = [b.men[0], b.women[0]].filter(Boolean);
        break;
      case 'mixed_2':
        participantAPlayers = [a.men[1], a.women[1]].filter(Boolean);
        participantBPlayers = [b.men[1], b.women[1]].filter(Boolean);
        break;
      case 'dreambreaker':
        // Real MLP Dreambreaker is played singles by just 1 of a team's 4
        // players — this picks a default (roster's first player), not a
        // captain's actual choice; the scorer can swap in a different one
        // live via the swap feature (enabled by default for this game type
        // below in generateMLPSchedule).
        participantAPlayers = rosterA[0] ? [rosterA[0]] : [];
        participantBPlayers = rosterB[0] ? [rosterB[0]] : [];
        break;
      default:
        participantAPlayers = [];
        participantBPlayers = [];
    }
    return { gameType, teamA, teamB, participantAPlayers, participantBPlayers };
  });
}

/**
 * Generates a full MLP schedule. Each round is one team-pairing's complete
 * 5-game slate (Men's Doubles, Women's Doubles, Mixed 1, Mixed 2,
 * Dreambreaker) — a team faces exactly one opponent per round, so there's no
 * need to cap how many games a team can be in "at once" the way interleaving
 * multiple pairings into a round would. `numberOfRounds === totalPairings`.
 *
 * `rosterByTeamId` — object of teamId -> array of {id, name, gender?}
 * players (gender optional/likely absent today — see splitRosterByGender).
 *
 * `options`:
 * - `useGrouping` + `numberOfGroups` + `groupAssignments` (participant id ->
 *   group index) — which teams are in which group. Required (both supported
 *   structures need groups defined) once there are 3+ teams; with exactly 2
 *   teams grouping doesn't change anything (one faceoff either way) so it's
 *   skipped even if passed.
 * - `structure` — MLP_STRUCTURES.GROUP_PLAYOFFS (round-robin within each
 *   group, then a playoff round) or MLP_STRUCTURES.CROSS_GROUP (each group
 *   only faces the other group(s), standings-only, no playoff). Defaults to
 *   CROSS_GROUP.
 * - `numberOfPlayoffGames` — only used for GROUP_PLAYOFFS; appends a
 *   Playoffs round of this many teams-TBD placeholder matches, resolved from
 *   standings later (same pattern as Custom Format's playoff round).
 */
export function generateMLPSchedule(participants, rosterByTeamId = {}, options = {}) {
  if (!participants || participants.length < 2) {
    throw new Error('At least 2 participants are required');
  }

  const structure = options.structure === MLP_STRUCTURES.GROUP_PLAYOFFS
    ? MLP_STRUCTURES.GROUP_PLAYOFFS
    : MLP_STRUCTURES.CROSS_GROUP;
  const useGrouping = !!options.useGrouping && participants.length > 2;
  const numberOfGroups = Number(options.numberOfGroups) || 2;
  const numberOfPlayoffGames = structure === MLP_STRUCTURES.GROUP_PLAYOFFS
    ? (Number.isFinite(Number(options.numberOfPlayoffGames)) ? Number(options.numberOfPlayoffGames) || 0 : 0)
    : 0;

  const teams = shuffleArray([...participants]);
  const n = teams.length;
  const groupAssignment = useGrouping ? assignGroups(teams, numberOfGroups, options.groupAssignments) : null;
  // GROUP_PLAYOFFS plays *within* each group; CROSS_GROUP plays only *across*
  // groups — same 'within'/'across' vocabulary Custom Format already uses.
  const groupMode = structure === MLP_STRUCTURES.GROUP_PLAYOFFS ? 'within' : 'across';

  // Every eligible team-pairing faces off exactly once, each a 5-game slate.
  // Without grouping (or exactly 2 teams): every pair is eligible. With
  // grouping: only pairs matching groupMode ('within' = same group, 'across'
  // = different groups) are eligible.
  const seriesQueues = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (groupAssignment) {
        const sameGroup = groupAssignment[i] === groupAssignment[j];
        const eligible = groupMode === 'within' ? sameGroup : !sameGroup;
        if (!eligible) continue;
      }
      const teamA = teams[i];
      const teamB = teams[j];
      const rosterA = (rosterByTeamId?.[teamA.id] || []).slice(0, 4);
      const rosterB = (rosterByTeamId?.[teamB.id] || []).slice(0, 4);
      seriesQueues.push({ teamA, teamB, games: buildMLPGamesForPairing(teamA, teamB, rosterA, rosterB) });
    }
  }

  if (seriesQueues.length === 0) {
    throw new Error(
      groupMode === 'within'
        ? 'No eligible pairings — at least one group needs 2+ teams for within-group play'
        : 'No eligible pairings — need at least 2 groups for cross-group play'
    );
  }

  const rounds = [];
  const matches = [];
  let matchNumber = 1;

  // One round per pairing — every game in that pairing's fixed 5-game slate
  // lands in the same round, in MLP_GAME_TYPES order.
  seriesQueues.forEach((series) => {
    const roundNumber = rounds.length + 1;
    const roundMatches = series.games.map((game) => ({
      id: generateMatchId(),
      matchNumber: matchNumber++,
      roundNumber,
      roundName: `Round ${roundNumber}`,
      roundType: 'league',
      teamA: game.teamA,
      teamB: game.teamB,
      gameType: game.gameType,
      gameTypeLabel: MLP_GAME_TYPE_LABELS[game.gameType],
      participantAPlayers: game.participantAPlayers,
      participantBPlayers: game.participantBPlayers,
      // Dreambreaker is played singles by 1 of a team's 4 players (unlike
      // the other 4 doubles games in the slate), to 21 points (unlike the
      // other games' 11), rally scoring (every point scores, not just the
      // serving side), and needs the scorer able to swap in a different
      // roster player live, since the 1 picked above is just a default,
      // not a captain's actual choice. swapOption FORCE_ON_TOTAL_POINTS_4
      // is the real MLP rule — both teams' players are forced to swap every
      // 4 total points — though the actual trigger logic in PickleballScoring
      // is separate, later work; this just plumbs the setting through.
      overriddenMatchSettings: game.gameType === 'dreambreaker'
        ? { participantsType: 'singles', swappable: true, maxPoints: 21, isRally: true, swapOption: SWAP_OPTIONS.FORCE_ON_TOTAL_POINTS_4 }
        : null,
      isBye: false,
      winner: null,
      teamAScore: null,
      teamBScore: null,
      status: 'pending',
      scheduledAt: null,
      venue: null,
    }));

    matches.push(...roundMatches);
    rounds.push({
      roundNumber,
      roundName: `Round ${roundNumber}`,
      roundType: 'league',
      matches: roundMatches,
    });
  });

  const numberOfLeagueRounds = rounds.length;

  // Group Stage + Playoffs: append a Playoffs round of teams-TBD placeholder
  // matches, resolved from standings once group play finishes — same
  // pattern as Custom Format's playoff round. Cross-group is standings-only,
  // no playoff round at all.
  if (structure === MLP_STRUCTURES.GROUP_PLAYOFFS && numberOfPlayoffGames > 0) {
    const playoffMatches = [];
    for (let p = 1; p <= numberOfPlayoffGames; p++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: numberOfLeagueRounds + 1,
        roundName: numberOfPlayoffGames === 1 ? 'Final' : `Playoff Game ${p}`,
        roundType: 'playoff',
        teamA: null,
        teamB: null,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      playoffMatches.push(match);
      matches.push(match);
    }
    rounds.push({
      roundNumber: numberOfLeagueRounds + 1,
      roundName: 'Playoffs',
      roundType: 'playoff',
      matches: playoffMatches,
    });
  }

  return {
    format: 'custom',
    formatName: 'MLP',
    numberOfRounds: numberOfLeagueRounds,
    gamesPerRound: MLP_GAMES_PER_TEAM_PAIRING, // every round is one pairing's full 5-game slate
    numberOfPlayoffGames,
    gamesPerTeam: MLP_GAMES_PER_TEAM_PAIRING,
    playersPerTeam: 4,
    useGrouping,
    numberOfGroups: useGrouping ? numberOfGroups : null,
    groupMode: useGrouping ? groupMode : null,
    groups: useGrouping
      ? Array.from({ length: numberOfGroups }, (_, g) =>
          teams.filter((_, idx) => groupAssignment[idx] === g)
        )
      : null,
    structure,
    rounds,
    matches,
    participants: teams,
    totalMatches: matches.length,
    warnings: [],
  };
}

// Real MLP is not "every possible 2-player combination from the roster" —
// it's a fixed slate per team pairing: Men's Doubles, Women's Doubles, Mixed
// Doubles 1, Mixed Doubles 2, plus a Dreambreaker tiebreaker game. That's 5
// games total, regardless of how the math would otherwise combinatorially
// work out for a 4-player roster (C(4,2) = 6, which is *not* what MLP plays).
const MLP_GAMES_PER_TEAM_PAIRING = 5;

/**
 * @param {number} teamsPerGroup - Number of teams in each group (e.g. 2
 *   Parsippany teams facing 2 Livingston teams -> teamsPerGroup = 2; a plain
 *   1-team-vs-1-team faceoff -> teamsPerGroup = 1).
 * @param {number} playersPerTeam - Number of players per team (always 4 for MLP).
 * @param {number} [y=1] - Number of times each specific team-pairing repeats.
 * @param {string} [structure=MLP_STRUCTURES.CROSS_GROUP] - Which pairing rule
 *   to estimate totals for. CROSS_GROUP: every team in one group faces every
 *   team in the other group(s) — teamsPerGroup² pairings (or, with 3+ groups,
 *   summed across every pair of groups). GROUP_PLAYOFFS: round-robin *within*
 *   each group — C(teamsPerGroup, 2) pairings per group, summed across all
 *   groups. Genuinely different totals, not just a label difference.
 * @param {number} [numberOfGroups=2] - Only matters for GROUP_PLAYOFFS (to
 *   sum within-group pairings across groups) and CROSS_GROUP with 3+ groups.
 * @returns {{ calculatedPairsPerTeam: number, gamesPerRound: number, totalRounds: number, totalMatches: number }}
 */
export function getMLPScheduleMetrics(teamsPerGroup, playersPerTeam, y = 1, structure = MLP_STRUCTURES.CROSS_GROUP, numberOfGroups = 2) {
  const pairsPerTeam = MLP_GAMES_PER_TEAM_PAIRING;

  let totalPairings;
  if (structure === MLP_STRUCTURES.GROUP_PLAYOFFS) {
    // Round-robin within each group: C(teamsPerGroup, 2) pairings per group,
    // summed across every group (equal-sized groups assumed).
    totalPairings = numberOfGroups * (teamsPerGroup * (teamsPerGroup - 1)) / 2;
  } else {
    // CROSS_GROUP: every team in one group faces every team in every other
    // group. For 2 groups that's teamsPerGroup²; for 3+ groups, sum that
    // over every pair of groups (C(numberOfGroups, 2) group-pairs).
    const groupPairCount = numberOfGroups >= 2 ? (numberOfGroups * (numberOfGroups - 1)) / 2 : 1;
    totalPairings = groupPairCount * teamsPerGroup * teamsPerGroup;
  }
  // MLP is always parallel/zip pairing: each team-pairing plays exactly the
  // fixed 5-game slate, never a full cross-product of every combination.
  const totalMatches = totalPairings * pairsPerTeam * y;

  // Every round is one team-pairing's complete 5-game slate — gamesPerRound
  // is always the fixed slate size, and totalRounds is one round per pairing
  // (times y repeats), not a team-count-dependent packing calculation.
  const gamesPerRound = pairsPerTeam;
  const totalRounds = totalPairings * y;

  return {
    calculatedPairsPerTeam: pairsPerTeam,
    gamesPerRound,
    totalRounds,
    totalMatches,
  };
}

/**
 * Format description card for the "Choose Tournament Format" UI — merged
 * into TOURNAMENT_FORMATS at the import site in ScheduleTab.jsx.
 */
export const MLP_FORMAT = {
  id: 'mlp',
  name: 'MLP',
  shortName: 'MLP',
  description: 'Major League Pickleball style — teams field multiple doubles pairs that face their opposing team\'s pairs straight-up, not every possible combination.',
  minTeams: 2,
  icon: 'zap',
  recommendedFor: 'Doubles tournaments with larger team rosters (e.g. 8 players, 2 play per game)',
};

// ── CLI ──────────────────────────────────────────────────────────────────
// Run directly with: node src/tournaments/utils/mlpScheduler.js \
//   --teamsPerGroup=2 --playersPerTeam=4 --y=1
//
// This file is also imported by ScheduleTab.jsx and bundled into the browser
// build, where `process` doesn't exist (or isn't the real Node process) —
// guard on that first so this block only ever runs under Node, never in-app.
// Not part of the app and unreachable from it, so it is not counted: the
// guard above is false under both the browser build and the test runner.
/* v8 ignore start */
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const args = Object.fromEntries(
    process.argv.slice(2).map(arg => {
      const [key, value] = arg.replace(/^--/, '').split('=');
      return [key, value];
    })
  );

  if (!args.teamsPerGroup || !args.playersPerTeam) {
    console.error(
      'Usage: node mlpScheduler.js --teamsPerGroup=2 --playersPerTeam=4 --y=1'
    );
    process.exit(1);
  }

  const params = {
    teamsPerGroup: Number(args.teamsPerGroup),
    playersPerTeam: Number(args.playersPerTeam),
    y: Number(args.y) || 1,
  };

  console.log('Input:', params);
  console.log('Result:', getMLPScheduleMetrics(
    params.teamsPerGroup, params.playersPerTeam, params.y
  ));
}
/* v8 ignore stop */
